import { useState, useEffect } from "react";
import axios from "axios";
import { History, Loader2, Check, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleLabels = { admin: "Admin", hr: "HR", finance: "Finance", manager: "Manager" };

const formatDT = (iso) => {
  try {
    return format(new Date(iso), "MMM dd, yyyy HH:mm:ss");
  } catch {
    return iso || "";
  }
};

const PermissionAuditDialog = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/permissions/audit?limit=200`);
      setEntries(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl border-[#E4E4E7] rounded-sm max-h-[85vh] overflow-y-auto"
        data-testid="permission-audit-dialog"
      >
        <DialogHeader className="border-b border-[#E4E4E7] pb-4">
          <DialogTitle
            className="text-2xl font-semibold tracking-tight flex items-center gap-2"
            style={{ fontFamily: "Manrope" }}
          >
            <History className="w-6 h-6" />
            Permission Change Log
          </DialogTitle>
          <DialogDescription className="text-sm text-[#71717A]">
            Every role permission toggle, most recent first. Append-only.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-[#71717A]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
          </div>
        ) : entries.length === 0 ? (
          <div className="py-10 text-center text-[#71717A]" data-testid="audit-empty">
            No permission changes yet.
          </div>
        ) : (
          <div className="py-2 space-y-1" data-testid="audit-list">
            {entries.map((e) => (
              <div
                key={e.id}
                className="grid grid-cols-[auto_1fr_auto] gap-3 items-center p-3 border border-[#E4E4E7] rounded-sm bg-white"
                data-testid={`audit-entry-${e.id}`}
              >
                <div
                  className={`w-8 h-8 rounded-sm flex items-center justify-center text-white ${
                    e.new_enabled ? "bg-[#059669]" : "bg-[#71717A]"
                  }`}
                >
                  {e.new_enabled ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#09090B] flex flex-wrap items-center gap-1">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-[#F4F4F5] text-[#71717A] border border-[#E4E4E7]">
                      {roleLabels[e.role] || e.role}
                    </span>
                    <span>{e.new_enabled ? "granted" : "revoked"}</span>
                    <span className="font-mono text-[11px] text-[#71717A]">{e.permission_key}</span>
                  </div>
                  <div className="text-[11px] text-[#71717A] mt-0.5">
                    by <span className="font-semibold text-[#09090B]">{e.changed_by_name || e.changed_by_username || "unknown"}</span>
                    {" • "}
                    {formatDT(e.changed_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-2 text-xs text-[#71717A] border-t border-[#E4E4E7] pt-3">
          {entries.length} event{entries.length !== 1 ? "s" : ""} shown
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PermissionAuditDialog;
